/**
 * RequireModule — gate de acceso a módulo RBAC.
 *
 * Envuelve una ruta para verificar que el usuario tenga el permiso `accion`
 * sobre `modulo`. Si no, redirige a /dashboard con un toast.
 *
 * Admin (es_admin=true) bypassa siempre.
 *
 * Defensa en profundidad: el backend también valida con @require_module en
 * los endpoints listing. Esto solo controla la visibilidad de la pantalla.
 */
import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import useAuthStore from '@/stores/authStore';

interface RequireModuleProps {
  modulo: string;
  accion?: string;
  children: React.ReactNode;
}

export const RequireModule = ({ modulo, accion = 'ver', children }: RequireModuleProps) => {
  const { canAccessModulo, isAuthenticated, isLoading } = useAuthStore();
  const allowed = canAccessModulo(modulo, accion);

  useEffect(() => {
    if (!isLoading && isAuthenticated && !allowed) {
      toast.error(`No tienes permiso para acceder a "${modulo}"`);
    }
  }, [isLoading, isAuthenticated, allowed, modulo]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!allowed) return <Navigate to="/dashboard" replace />;

  return <>{children}</>;
};

export default RequireModule;
